import React, { useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { EditorProvider } from '../contexts/EditorContext';
import EditorLayout from '../components/editor/EditorLayout';
import { useTemplateLoader } from '../hooks/useTemplateLoader';
import { useEditor } from '../contexts/EditorContext';
import { TemplateSEO } from '../components/layout/SEO';  
import { HelmetProvider } from 'react-helmet-async';  
const EditorContent = () => {
  const { templateId } = useParams();
  const navigate = useNavigate();
  const { state, actions, canUndo, canRedo } = useEditor();
  const { loadTemplate, loading: templateLoading, error } = useTemplateLoader();
  
  const hasLoadedRef = useRef(false);
  const currentTemplateIdRef = useRef(templateId);
  const isLoadingRef = useRef(false);
  const loadAttemptedRef = useRef(false);
  const currentTemplateRef = useRef(null);
  const loadInitialTemplate = useCallback(async () => {
    if (isLoadingRef.current || loadAttemptedRef.current) return;
    
    if (hasLoadedRef.current && templateId === currentTemplateIdRef.current) {
      return;
    }

    console.log('Loading template:', templateId);
    
    try {
      isLoadingRef.current = true;
      loadAttemptedRef.current = true;
      actions.setLoading(true);
      
      const templateToLoad = templateId || 'professional-sidebar';
      currentTemplateIdRef.current = templateToLoad;
      
      const template = await loadTemplate(templateToLoad);
      currentTemplateRef.current = template;      
      console.log('Template loaded successfully:', template.name);
      
      const newPage = {
        id: Date.now(),
        title: template.name || 'Template',
        content: template.content || '<div>Empty template</div>',
        elements: []
      };
      
      actions.loadTemplate({
        ...template,
        pages: [newPage]
      });
      
      hasLoadedRef.current = true;
      actions.setHasLoaded(true);
    } catch (error) {
      console.error('Failed to load template:', error);
      
      // Load minimal default template
      const defaultTemplate = {
        id: 'default',
        name: 'Default Template',
        content: `
          <div class="resume-content" style="font-family: 'Inter', sans-serif; padding: 40px; max-width: 800px; margin: 0 auto;">
            <div class="header-section" style="text-align: center; margin-bottom: 40px; padding-bottom: 30px; border-bottom: 2px solid #1ca29b;">
              <h1 style="font-size: 36px; color: #1a365d; margin-bottom: 10px; font-weight: 800;">YOUR NAME HERE</h1>
              <p style="font-size: 20px; color: #1ca29b; margin-bottom: 20px; font-weight: 600;">Your Job Title</p>
              <div style="display: flex; justify-content: center; gap: 20px; margin-top: 20px; flex-wrap: wrap; color: #4a5568;">
                <span>📧 email@example.com</span>
                <span>📱 +1 (555) 123-4567</span>
                <span>📍 City, Country</span>
              </div>
            </div>
            
            <div class="section" style="margin-bottom: 30px;">
              <h2 style="color: #1ca29b; margin-bottom: 15px; font-size: 22px; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px;">PROFESSIONAL SUMMARY</h2>
              <p style="line-height: 1.6; color: #4a5568; font-size: 15px;">
                Experienced professional with a proven track record. Skilled in problem-solving and innovation.
              </p>
            </div>
            
            <div class="section" style="margin-bottom: 30px;">
              <h2 style="color: #1ca29b; margin-bottom: 15px; font-size: 22px; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px;">EXPERIENCE</h2>
              <div style="margin-bottom: 20px;">
                <h3 style="font-size: 18px; color: #1a365d; margin-bottom: 5px;">Job Title</h3>
                <p style="color: #64748b; margin-bottom: 10px; font-style: italic;">Company Name | 2020 - Present</p>
                <ul style="color: #4a5568; line-height: 1.8;">
                  <li>Achievement or responsibility</li>
                  <li>Achievement or responsibility</li>
                </ul>
              </div>
            </div>
            
            <div class="section" style="margin-bottom: 30px;">
              <h2 style="color: #1ca29b; margin-bottom: 15px; font-size: 22px; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px;">EDUCATION</h2>
              <div>
                <h3 style="font-size: 18px; color: #1a365d; margin-bottom: 5px;">Degree</h3>
                <p style="color: #64748b; font-style: italic;">University Name | Year</p>
              </div>
            </div>
            
            <div class="section">
              <h2 style="color: #1ca29b; margin-bottom: 15px; font-size: 22px; border-bottom: 1px solid #e2e8f0; padding-bottom: 8px;">SKILLS</h2>
              <div style="display: flex; flex-wrap: wrap; gap: 10px;">
                <span style="background: #e0f2fe; color: #0369a1; padding: 6px 14px; border-radius: 20px; font-size: 14px;">Skill 1</span>
                <span style="background: #e0f2fe; color: #0369a1; padding: 6px 14px; border-radius: 20px; font-size: 14px;">Skill 2</span>
                <span style="background: #e0f2fe; color: #0369a1; padding: 6px 14px; border-radius: 20px; font-size: 14px;">Skill 3</span>
              </div>
            </div>
          </div>
        `,
        design: {
          fontFamily: 'Inter, system-ui',
          fontSize: 14,
          lineHeight: 1.6,
          letterSpacing: 'normal',
          accentColor: '#1ca29b',
          backgroundColor: '#ffffff',
          textColor: '#2d3748',
          padding: '40px',
          margin: '0px'
        }
      };
      
      const defaultPage = {
        id: Date.now(),
        title: 'Default Template',
        content: defaultTemplate.content,
        elements: []
      };
      
      actions.loadTemplate({
        ...defaultTemplate,
        pages: [defaultPage]
      });
      
      hasLoadedRef.current = true;
      actions.setHasLoaded(true);
    } finally {
      actions.setLoading(false);
      isLoadingRef.current = false;
    }
  }, [templateId, actions, loadTemplate]);

  useEffect(() => {
    if (!hasLoadedRef.current || templateId !== currentTemplateIdRef.current) {
      loadInitialTemplate();
    }
    
    return () => {
      currentTemplateIdRef.current = templateId;
    };
  }, [templateId, loadInitialTemplate]);

  useEffect(() => {
    return () => {
      loadAttemptedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        if (canUndo) actions.undo();
      }
      if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.shiftKey && e.key === 'Z'))) {
        e.preventDefault();
        if (canRedo) actions.redo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [actions, canUndo, canRedo]);

  const showLoading = state.isLoading && !hasLoadedRef.current;

  if (showLoading || templateLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="spinner"></div>
          <p>Loading template...</p>
        </div>
      </div>
    );
  }

  if (error && !hasLoadedRef.current) {
    return (
      <div className="error-screen">
        <div className="error-content">
          <i className="fas fa-exclamation-triangle"></i>
          <h3>Failed to load template</h3>
          <p>{error.message || error}</p>
          <button onClick={() => navigate('/templates')} className="btn primary">
            Back to Templates
          </button>
        </div>
      </div>
    );
  }

  if (!hasLoadedRef.current) {
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="spinner"></div>
          <p>Preparing editor...</p>
        </div>
      </div>
    );
  }

  return (
    <HelmetProvider>
      <div>
        <TemplateSEO template={currentTemplateRef.current} />
        <EditorLayout />
      </div>
    </HelmetProvider>
  );
};

const EditorPage = () => {
  return (
    <EditorProvider>
      <EditorContent />
    </EditorProvider>
  );
};

export default EditorPage;